import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SalonNote, SalonNoteCreateRequest, SalonNotesResponse, SalonNoteUpdateRequest } from '@manaro/contracts'
import { queryKeys } from '@/shared/api/query-keys.ts'
import { createSalonNote, deleteSalonNote, fetchDueReminders, fetchSalonNotes, updateSalonNote } from './api.ts'
import { replaceNote, withNote, withoutNote } from './notes.ts'
import { markRemindersSeen, reminderKey } from './seen-reminders.ts'

/** Colleagues write at the same time: a short freshness window, refetched when the tab comes back. */
const NOTES_STALE_MS = 30 * 1000
/** A reminder is a day, not a minute: a few checks an hour catch the new day in a tab left open overnight. */
const REMINDERS_POLL_MS = 10 * 60 * 1000

export function useSalonNotes() {
  return useQuery({
    queryKey: queryKeys.salonNotes,
    queryFn: ({ signal }) => fetchSalonNotes(signal),
    staleTime: NOTES_STALE_MS,
    refetchOnWindowFocus: true,
  })
}

/** The signed-in person's reminders due today or earlier; only for those who see «Салоны». */
export function useDueReminders(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.salonReminders,
    queryFn: ({ signal }) => fetchDueReminders(signal),
    enabled,
    staleTime: NOTES_STALE_MS,
    refetchInterval: REMINDERS_POLL_MS,
    refetchOnWindowFocus: true,
  })
}

function useNotesCache() {
  const queryClient = useQueryClient()

  return {
    // A refetch started before the write would land after the patch and bring the old list back: drop it first.
    // Any write can add, move or close a reminder, so the reminder list is asked again.
    patch: async (update: (data: SalonNotesResponse) => SalonNotesResponse) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.salonNotes })
      queryClient.setQueryData<SalonNotesResponse>(queryKeys.salonNotes, (data) => data && update(data))
      void queryClient.invalidateQueries({ queryKey: queryKeys.salonReminders })
    },
    // A lost answer or a note a colleague already removed: reload what is really there.
    reload: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.salonNotes }),
        queryClient.invalidateQueries({ queryKey: queryKeys.salonReminders }),
      ]),
  }
}

export function useAddSalonNote() {
  const cache = useNotesCache()

  return useMutation({
    mutationFn: (request: SalonNoteCreateRequest) => createSalonNote(request),
    onSuccess: (note: SalonNote) => {
      if (note.remindOn) markRemindersSeen([reminderKey(note)])
      return cache.patch((data) => withNote(data, note))
    },
    onError: cache.reload,
  })
}

/** Text, reminder day or «done» on one's own note. */
export function useUpdateSalonNote() {
  const cache = useNotesCache()

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: SalonNoteUpdateRequest }) => updateSalonNote(id, patch),
    onSuccess: (note: SalonNote) => {
      if (note.remindOn) markRemindersSeen([reminderKey(note)])
      return cache.patch((data) => replaceNote(data, note))
    },
    onError: cache.reload,
  })
}

export function useDeleteSalonNote() {
  const cache = useNotesCache()

  return useMutation({
    mutationFn: (id: string) => deleteSalonNote(id),
    onSuccess: (_result: void, id: string) => cache.patch((data) => withoutNote(data, id)),
    onError: cache.reload,
  })
}
